import { Component, h, Prop, Event, EventEmitter, State, Watch } from '@stencil/core';
import AuthHeader from '../auth-header';
import { updateFieldValidations } from '../utils';
import { PRIVACY_URLS, TERMS_URLS } from '../../../utils/constants';

const SocialSignInButton = ({ icon, text, url, onClick }: { icon: string, text: string, url: string, onClick?: () => void }) => {
  return (
    <a class="social-signin-btn" href={url} onClick={onClick}>
      <span class="icon">
        <hd-icon svg={icon} width="24" height="24" />
      </span>
      <span>{text}</span>
    </a>
  )
}

@Component({
  tag: 'hd-auth-provider',
  shadow: false
})
export class HdAuthProvider {
  @Prop({ mutable: true }) type: "signup" | "login" | "recover" | "check-inbox" = "signup";
  @Prop() googleSignInUrl: string = '';
  @Prop() facebookSignInUrl: string = '';
  @Prop() email: string = '';
  @Prop() password: string = '';
  /**
   * Errors after submission
   * @example { email: "Email already in use", password: "Invalid password" }
   */
  @Prop() localErrors: Record<string, string> = {};
  @Event() nextStep: EventEmitter<Record<string, string | boolean>>;
  @Event() recoverStep: EventEmitter<Record<string, string | boolean>>;
  @State() showPassword = false;
  @State() loading = false;
  @State() errorFormFields: Record<string, boolean | null> = {
    email: null,
    password: null,
  }
  @State() isFormValid = false;
  @Event() loggedInChange: EventEmitter<boolean>;
  @Event() provider: EventEmitter<string>;

  emailError!: HTMLParagraphElement;
  passwordError?: HTMLParagraphElement;

  private validationTimeouts: Record<string, number> = {};

  componentWillLoad() {
    this.errorFormFields = {
      email: null,
      password: this.type === "login" ? null : true,
    }
  }

  @Watch("type")
  typeChanged(newType: "signup" | "login") {
    this.errorFormFields.password = newType === "login" ? null : true;
    this.loading = false;
  }

  @Watch('localErrors')
  localErrorsChanged(newLocalErrors: Record<string, string>) {
    if (newLocalErrors["email"]) {
      this.emailError.textContent = newLocalErrors["email"];
      this.emailError.closest(".form-group")?.classList.add("has-error");
    }
    if (newLocalErrors["password"] && this.passwordError) {
      this.passwordError.textContent = newLocalErrors["password"];
      this.passwordError.closest(".form-group")?.classList.add("has-error");
    }
    this.loading = false;
  }

  buildSocialSignInUrl(baseUrl: string) {
    const url = new URL(window.location.origin + baseUrl);
    url.searchParams.set('site', "us");

    if (window.AFD?.countryCode) {
      url.searchParams.set('country', window.AFD.countryCode);
    }

    const redirectUri = new URL(window.location.href);
    redirectUri.search = "";
    redirectUri.searchParams.set('auth', 'hadid');
    url.searchParams.set('redirect_uri', redirectUri.toString());
    return url.toString();
  }

  updateField(field: string, e: Event) {
    const target = e.target as HTMLInputElement;
    this[field] = target.value;

    if (this.validationTimeouts[field]) {
      clearTimeout(this.validationTimeouts[field]);
    }

    this.validationTimeouts[field] = window.setTimeout(() => {
      this.validateField(field, target);
    }, 500);
  }

  validateField = (field: string, target: HTMLInputElement) => {
    const isValid = updateFieldValidations(field, target);
    this.errorFormFields[field] = isValid;
    this.isFormValid = Object.values(this.errorFormFields).every(isValid => isValid === true);
  }

  submit = () => {
    this.loading = true;
    this.provider.emit("email");

    if (this.type === "login") {
      this.loggedInChange.emit(true);
    }
    this.nextStep.emit({ type: this.type, email: this.email, password: this.type === "login" ? this.password : undefined });
  }

  trackExternalProviderLogin = (provider: string) => {
    this.provider.emit(provider);
    if (this.type === "signup") return;
    this.loggedInChange.emit(true);
  }

  render() {
    return (
      <div class="step-container auth-provider">
        {this.type === "signup" && (
          <AuthHeader
            title="Create Your ArchDaily Account"
            subhead="Gain access to curated insights, professional tools, and daily inspiration to elevate your work."
          />)}
        {this.type === "login" && (
          <AuthHeader
            title="Log In to Your ArchDaily Account"
            subhead="Gain access to curated insights, professional tools, and daily inspiration to elevate your work."
          />)}
        <SocialSignInButton icon="google" text="Continue with Google"
          url={this.buildSocialSignInUrl(this.googleSignInUrl)}
          onClick={() => this.trackExternalProviderLogin("Google")} />
        {this.type === "login" && <SocialSignInButton icon="facebook_blue" text="Continue with Facebook"
          url={this.buildSocialSignInUrl(this.facebookSignInUrl)}
          onClick={() => this.trackExternalProviderLogin("Facebook")} />}
        <div class="divider">
          <span>or use your email</span>
        </div>
        <form class="provider-form">
          <div class="form-group">
            <label htmlFor="email">Email</label>
            <input type="email" id="email"
              value={this.email}
              onInput={(e) => this.updateField("email", e)}
              onBlur={(e) => this.validateField("email", e.target as HTMLInputElement)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();

                  this.isFormValid && this.submit();
                }
              }}
            />
            <p ref={el => this.emailError = el} class="error-message"></p>
          </div>
          {
            this.type === "login" && (
              <div class="form-group">
                <div class="password-label-container">
                  <label htmlFor="password">Password</label>
                  <p onClick={() => this.recoverStep.emit()}>Forgot your password?</p>
                </div>
                <div class="password-input-container">
                  <input type={this.showPassword ? "text" : "password"} id="password"
                    value={this.password}
                    onInput={(e) => this.updateField("password", e)}
                    onBlur={(e) => this.validateField("password", e.target as HTMLInputElement)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();

                        this.isFormValid && this.submit();
                      }
                    }}
                  />
                  <hd-icon svg={this.showPassword ? "eye_closed" : "eye_open"} width="10" height="10" onClick={() => this.showPassword = !this.showPassword} />
                </div>
                <p ref={el => this.passwordError = el} class="error-message"></p>
              </div>
            )
          }

          <div class="form-actions">
            {this.type === "signup" && (
              <p class="terms">By creating an account, you agree to our <a href={TERMS_URLS} target="_blank">Terms of Service</a> and <a href={PRIVACY_URLS} target="_blank">Privacy Policy</a>.</p>
            )}
            <hd-button primary padded
              disabled={!this.isFormValid || this.loading}
              onClick={this.submit}>
              {this.loading ? <hd-icon svg="loader_bouncing_circles" width="24" height="24" /> : 'Continue with email'}
            </hd-button>
            {this.type === "signup" && (
              <p class="login-link">Already have a free account or paid plan? <span onClick={() => this.type = "login"}>Log In</span></p>
            )}
            {this.type === "login" && (
              <p class="login-link">Don't have an account? <span onClick={() => this.type = "signup"}>Sign Up</span></p>
            )}
          </div>
        </form>
      </div>
    )
  }
};
